import { Alignment } from './models';

export interface PresentedAlignment extends Alignment {
  hunkId: string;
}

/** Assign stable, Git-like hunk labels only to rows with a meaningful change. */
export function presentAlignments(items: Alignment[]): PresentedAlignment[] {
  let hunk = 0;
  return items.map(item => ({
    ...item,
    hunkId: item.status === 'unchanged' ? '—' : item.hunkId || `hunk-${++hunk}`,
  }));
}

export function comparisonRowColor(status: Alignment['status']): string {
  switch (status) {
    case 'only_in_b': return '#dcfce0';
    case 'only_in_a': return '#fee2e2';
    case 'modified': return '#fff3c4';
    case 'timing_only': return '#e6f0ff';
    default: return '#ffffff';
  }
}

const formatValue = (value: string | undefined) => value === undefined ? '—' : value;

export function changeSummary(item: Alignment, leftLabel = 'OPUS A', rightLabel = 'OPUS B'): string {
  if (!item.a) return `Command added in ${rightLabel}${item.b ? `: ${item.b.command}` : ''}`;
  if (!item.b) return `Command removed from ${rightLabel}${item.a ? `: ${item.a.command}` : ''}`;
  const parameterChanges = item.changedFields.map(field => {
    const before = item.a?.parameters[field]; const after = item.b?.parameters[field];
    if (field.toLowerCase().includes('asserted_vector_array')) return `Asserted vector changed (${leftLabel}: ${before?.split(',').filter(Boolean).length || 0} values; ${rightLabel}: ${after?.split(',').filter(Boolean).length || 0} values)`;
    if ((before?.length || 0) > 80 || (after?.length || 0) > 80) return `${field}: long value changed`;
    return `${field}: ${formatValue(before)} → ${formatValue(after)}`;
  });
  if (parameterChanges.length) return parameterChanges.join('; ');
  if (item.differenceTypes.includes('resource_or_category_change')) return `${item.a.resource} → ${item.b.resource}`;
  if (item.differenceTypes.includes('planning_duration_change')) return `Modeled duration: ${item.a.planningDurationS ?? 0}s → ${item.b.planningDurationS ?? 0}s`;
  return item.a.command;
}

export function signedDrift(item: Alignment, leftLabel = 'OPUS A', rightLabel = 'OPUS B'): string | null {
  if (!item.a || !item.b || !item.differenceTypes.includes('start_time_drift')) return null;
  const value = item.b.planningStartS - item.a.planningStartS;
  return `${rightLabel} starts ${Math.abs(value).toFixed(1)} s ${value >= 0 ? 'after' : 'before'} ${leftLabel}`;
}
